import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Delete, ContentCopy, CheckCircle } from '@mui/icons-material';
import { invitationsApi } from '../services/api';
import { Invitation } from '../types';

const Invitations: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState(3); // 1=Admin, 2=Editor, 3=Viewer
  const [createdInvitation, setCreatedInvitation] = useState<Invitation | null>(null);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const data = await invitationsApi.getInvitations();
      setInvitations(data);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async () => {
    if (!newEmail) {
      setError('Please enter an email address');
      return;
    }

    setError(null);
    try {
      const invitation = await invitationsApi.createInvitation({
        email: newEmail,
        assignedRole: selectedRole
      });
      setCreatedInvitation(invitation);
      setNewEmail('');
      setSelectedRole(3); // Reset to Viewer
      setSuccess('Invitation created successfully!');
      await fetchInvitations();
    } catch (err: any) {
      console.error('Failed to create invitation:', err);
      setError(err.response?.data?.message || 'Failed to create invitation');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) {
      return;
    }

    try {
      await invitationsApi.deleteInvitation(id);
      setSuccess('Invitation deleted successfully');
      await fetchInvitations();
    } catch (err) {
      console.error('Failed to delete invitation:', err);
      setError('Failed to delete invitation');
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setSuccess('Invite code copied to clipboard!');
    setTimeout(() => setSuccess(null), 3000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const isExpired = (dateString: string) => {
    return new Date(dateString) < new Date();
  };

  const getRoleName = (roleValue: string | number): string => {
    const roleStr = String(roleValue).toLowerCase();
    if (roleStr === '1' || roleStr === 'admin') return 'Admin';
    if (roleStr === '2' || roleStr === 'editor') return 'Editor';
    if (roleStr === '3' || roleStr === 'viewer') return 'Viewer';
    return String(roleValue);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Invitation Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create and manage invitations for new users
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Create New Invitation
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="friend@example.com"
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                label="Role"
                onChange={(e) => setSelectedRole(Number(e.target.value))}
              >
                <MenuItem value={1}>Admin</MenuItem>
                <MenuItem value={2}>Editor</MenuItem>
                <MenuItem value={3}>Viewer</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateInvitation}
            >
              Create
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            All Invitations
          </Typography>

          {loading ? (
            <Typography>Loading...</Typography>
          ) : invitations.length === 0 ? (
            <Typography color="text.secondary">
              No invitations yet. Create one above to invite your first user!
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Invite Code</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Used By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <code>{invitation.inviteCode}</code>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyCode(invitation.inviteCode)}
                            title="Copy to clipboard"
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={getRoleName(invitation.assignedRole)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {invitation.isUsed ? (
                          <Chip label="Used" color="success" size="small" icon={<CheckCircle />} />
                        ) : isExpired(invitation.expiresAt) ? (
                          <Chip label="Expired" color="error" size="small" />
                        ) : (
                          <Chip label="Active" color="primary" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                      <TableCell>{formatDate(invitation.expiresAt)}</TableCell>
                      <TableCell>
                        {invitation.usedByUserEmail || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {!invitation.isUsed && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(invitation.id)}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Success Dialog for new invitation */}
      <Dialog open={!!createdInvitation} onClose={() => setCreatedInvitation(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Invitation Created Successfully!</DialogTitle>
        <DialogContent>
          {createdInvitation && (
            <Box>
              <Typography variant="body1" gutterBottom>
                An invitation has been created for <strong>{createdInvitation.email}</strong>
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Invite Code:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" component="code">
                    {createdInvitation.inviteCode}
                  </Typography>
                  <IconButton
                    onClick={() => handleCopyCode(createdInvitation.inviteCode)}
                    color="primary"
                  >
                    <ContentCopy />
                  </IconButton>
                </Box>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Send this code to {createdInvitation.email}. It expires on{' '}
                {formatDate(createdInvitation.expiresAt)}.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedInvitation(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Invitations;
